import React, { useState } from 'react';
import { useEffect } from 'react';
import { toast } from 'react-toastify';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Card,
  CardContent,
  Grid,
  Alert,
  TextField,
} from '@mui/material';
import {
  Login as CheckInIcon,
  Logout as CheckOutIcon,
  AccessTime as ClockIcon,
} from '@mui/icons-material';
import { checkIn, checkOut, fetchAttendance, resetCheckStatus } from '../../store/slices/attendanceSlice';
import { geoLocationApi } from '../../api/geoLocationApi';
import { AppDispatch, RootState } from '../../store/store';

const AttendancePanel: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { records, loading, checkInStatus, checkOutStatus, error } = useSelector(
    (state: RootState) => state.attendance
  );

  const [employeeId, setEmployeeId] = useState('');
  const [startDate, setStartDate] = useState(
    new Date(new Date().getFullYear(), new Date().getMonth(), 1).toISOString().split('T')[0]
  );
  const [endDate, setEndDate] = useState(new Date().toISOString().split('T')[0]);

  const [hasBiometricCheckIn, setHasBiometricCheckIn] = useState(false);
  const [biometricNotified, setBiometricNotified] = useState(false);

  useEffect(() => {
    // Check for biometric check-in in today's records
    const today = new Date().toISOString().split('T')[0];
    const biometricCheckIn = records.some(
      (rec) =>
        String(rec.type || '').toUpperCase() === 'CHECK IN' &&
        String(rec.modeOfEntry || '').toLowerCase().includes('biometric') &&
        String(rec.date || '').startsWith(today)
    );
    setHasBiometricCheckIn(biometricCheckIn);
    if (biometricCheckIn && !biometricNotified) {
      toast.info('You are already signed in by biometric');
      setBiometricNotified(true);
    }
    if (!biometricCheckIn && biometricNotified) {
      setBiometricNotified(false);
    }
  }, [records, biometricNotified]);

  const captureAndSaveLocation = (empId: string, source: string) => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          geoLocationApi.saveLocation({
            employeeId: empId,
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
            source,
          }).catch(() => {});
        },
        () => {
          // Geolocation denied or unavailable — save without coordinates
          geoLocationApi.saveLocation({
            employeeId: empId,
            latitude: 0,
            longitude: 0,
            locationName: 'Location unavailable',
            source,
          }).catch(() => {});
        }
      );
    }
  };

  const handleCheckIn = () => {
    if (hasBiometricCheckIn) {
      toast.info('You are already signed in by biometric');
      return;
    }
    if (employeeId) {
      dispatch(checkIn(employeeId));
      captureAndSaveLocation(employeeId, 'Web Check-In');
    }
  };

  const handleCheckOut = () => {
    if (employeeId) {
      dispatch(checkOut(employeeId));
      captureAndSaveLocation(employeeId, 'Web Check-Out');
    }
  };

  const handleFetchAttendance = () => {
    if (employeeId) {
      dispatch(fetchAttendance({ employeeId, startDate, endDate }));
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
    switch (status.toLowerCase()) {
      case 'present':
        return 'success';
      case 'halfday':
        return 'warning';
      case 'absent':
        return 'error';
      default:
        return 'default';
    }
  };

  return (
    <Box>
      <Typography variant="h5" fontWeight="bold" mb={3}>
        Attendance
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {checkInStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(resetCheckStatus())}>
          Checked in successfully!
        </Alert>
      )}
      {checkOutStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => dispatch(resetCheckStatus())}>
          Checked out successfully!
        </Alert>
      )}

      {/* Check-in / Check-out Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 4 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <ClockIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>
                Quick Actions
              </Typography>
              <TextField
                label="Employee ID"
                size="small"
                fullWidth
                value={employeeId}
                onChange={(e) => setEmployeeId(e.target.value)}
                sx={{ mb: 2 }}
                helperText="Enter employee GUID"
              />
              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                <Button
                  variant="contained"
                  color="success"
                  startIcon={<CheckInIcon />}
                  onClick={handleCheckIn}
                  disabled={checkInStatus === 'loading' || !employeeId || hasBiometricCheckIn}
                >
                  {checkInStatus === 'loading' ? <CircularProgress size={20} /> : 'Check In'}
                </Button>
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<CheckOutIcon />}
                  onClick={handleCheckOut}
                  disabled={checkOutStatus === 'loading' || !employeeId}
                >
                  {checkOutStatus === 'loading' ? <CircularProgress size={20} /> : 'Check Out'}
                </Button>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Attendance Report
              </Typography>
              <Grid container spacing={2} alignItems="center">
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="Start Date"
                    type="date"
                    fullWidth
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <TextField
                    label="End Date"
                    type="date"
                    fullWidth
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 4 }}>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleFetchAttendance}
                    disabled={!employeeId}
                  >
                    Load Report
                  </Button>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Attendance Table */}
      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell><strong>Date</strong></TableCell>
                <TableCell><strong>Employee</strong></TableCell>
                <TableCell><strong>Time</strong></TableCell>
                <TableCell><strong>Type</strong></TableCell>
                <TableCell><strong>Mode</strong></TableCell>
                <TableCell><strong>Location</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {records.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">
                      No attendance records. Enter Employee ID and load the report.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                records.map((record) => (
                  <TableRow key={record.id} hover>
                    <TableCell>{new Date(record.date).toLocaleDateString()}</TableCell>
                    <TableCell>{record.employeeName}</TableCell>
                    <TableCell>{record.time || '-'}</TableCell>
                    <TableCell>{record.type || '-'}</TableCell>
                    <TableCell>{record.modeOfEntry || '-'}</TableCell>
                    <TableCell>{record.locationName || '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={record.type || 'N/A'}
                        color={record.type?.toLowerCase() === 'checkin' ? 'success' : 'info'}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        )}
      </TableContainer>
    </Box>
  );
};

export default AttendancePanel;
